import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Users, CalendarDays, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import EventCard from '../components/EventCard';
import PublicEventCard from '../components/PublicEventCard';
import { EVENT_CATEGORIES } from '../lib/eventData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dateRange = (key) => {
  const now = new Date();
  if (key === 'today') {
    const end = new Date(now); end.setHours(23, 59, 59, 0);
    return { date_from: now.toISOString(), date_to: end.toISOString() };
  }
  if (key === 'week') {
    const end = new Date(now.getTime() + 7 * 86400000);
    return { date_from: now.toISOString(), date_to: end.toISOString() };
  }
  if (key === 'month') {
    const end = new Date(now.getTime() + 30 * 86400000);
    return { date_from: now.toISOString(), date_to: end.toISOString() };
  }
  return {};
};

export default function EventsPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  const [mode, setMode] = useState('public'); // 'public' | 'roles'
  const [tab, setTab] = useState('available');
  const [region, setRegion] = useState('all');
  const [category, setCategory] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [publicEvents, setPublicEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionOpts, setRegionOpts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/locations`, { params: { type: 'region', limit: 20 } })
      .then(({ data }) => setRegionOpts(data.locations || [])).catch(() => {});
  }, []);

  const fetchPublic = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...dateRange(dateFilter) };
      if (region !== 'all') params.region = region;
      if (category !== 'all') params.category = category;
      const { data } = await axios.get(`${API}/events/public`, { params });
      setPublicEvents(data);
    } catch (e) { setPublicEvents([]); } finally { setLoading(false); }
  }, [region, category, dateFilter]);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === 'organized') params.mine = 'organized';
      else if (tab === 'applied') params.mine = 'applied';
      else {
        params.status = 'open';
        if (region !== 'all') params.region = region;
        if (category !== 'all') params.category = category;
      }
      const { data } = await axios.get(`${API}/events`, { params, withCredentials: true });
      setEvents(data);
    } catch (e) { setEvents([]); } finally { setLoading(false); }
  }, [tab, region, category]);

  useEffect(() => {
    if (mode === 'public') fetchPublic();
    else fetchRoles();
  }, [mode, fetchPublic, fetchRoles]);

  const pill = (active) => `inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${active ? 'bg-[#D97736] text-white shadow-sm' : 'bg-white text-[#5C5C5C] border border-[#E5E4E0] hover:border-[#D97736] hover:text-[#D97736]'}`;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="events-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-2">
        <h1 className="font-outfit text-3xl font-bold text-[#121212] mb-4">{tr('Pasākumi', 'Events', 'События')}</h1>
        <div className="flex flex-wrap gap-2" data-testid="events-mode-tabs">
          <button className={pill(mode === 'public')} onClick={() => setMode('public')} data-testid="events-mode-public">
            <CalendarDays className="w-4 h-4" /> {tr('Latvijas pasākumi', 'Latvia events', 'События Латвии')}
          </button>
          <button className={pill(mode === 'roles')} onClick={() => setMode('roles')} data-testid="events-mode-roles">
            <Users className="w-4 h-4" /> {tr('Lomas un pieteikumi', 'Roles & applications', 'Роли и заявки')}
          </button>
          <Link to="/calendar" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-[#4A6B53] text-white hover:bg-[#3d5944] transition-all" data-testid="events-my-calendar-link">
            <CalendarDays className="w-4 h-4" /> {tr('Mans kalendārs', 'My calendar', 'Мой календарь')}
          </Link>
        </div>
      </div>

      {mode === 'roles' && (
        <div className="bg-gradient-to-br from-[#4A6B53] to-[#3d5944] text-white mt-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-center gap-2 text-white/90 font-semibold mb-3">
              <Users className="w-5 h-5" />
              <span className="uppercase tracking-wider text-sm">{tr('Lomas un pieteikumi', 'Roles & applications', 'Роли и заявки')}</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold font-outfit mb-4 max-w-2xl">
              {tr('Veido pasākumu, atrodi komandu', 'Create an event, find your team', 'Создай событие, найди команду')}
            </h2>
            <p className="text-white/80 text-base md:text-lg max-w-xl mb-6 font-figtree">
              {tr('Meklē fotogrāfus, viesmīļus, mūziķus un citus dalībniekus konkrētām lomām tavā pasākumā.',
                'Recruit photographers, waiters, musicians and more for specific roles at your event.',
                'Найдите фотографов, официантов, музыкантов для ролей на вашем мероприятии.')}
            </p>
            <Link to="/events/create">
              <Button className="bg-white text-[#4A6B53] hover:bg-white/90" data-testid="create-event-btn">
                <Plus className="w-4 h-4 mr-1" /> {tr('Izveidot pasākumu', 'Create event', 'Создать событие')}
              </Button>
            </Link>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {mode === 'roles' && (
          <Tabs value={tab} onValueChange={setTab} className="mb-6">
            <TabsList data-testid="event-tabs">
              <TabsTrigger value="available" data-testid="event-tab-available">{tr('Pieejamie', 'Available', 'Доступные')}</TabsTrigger>
              {user && <TabsTrigger value="organized" data-testid="event-tab-organized">{tr('Mani pasākumi', 'My events', 'Мои события')}</TabsTrigger>}
              {user && <TabsTrigger value="applied" data-testid="event-tab-applied">{tr('Mani pieteikumi', 'My applications', 'Мои заявки')}</TabsTrigger>}
            </TabsList>
          </Tabs>
        )}

        {(mode === 'public' || tab === 'available') && (
          <div className="flex flex-wrap gap-3 mb-6">
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="w-48" data-testid="event-filter-region"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tr('Visi reģioni', 'All regions', 'Все регионы')}</SelectItem>
                {regionOpts.map((r) => <SelectItem key={r.slug} value={r.slug}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-56" data-testid="event-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tr('Visas kategorijas', 'All categories', 'Все категории')}</SelectItem>
                {Object.entries(EVENT_CATEGORIES).map(([k, c]) => <SelectItem key={k} value={k}>{c[language] || c.lv}</SelectItem>)}
              </SelectContent>
            </Select>
            {mode === 'public' && (
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-44" data-testid="event-filter-date"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{tr('Jebkurš datums', 'Any date', 'Любая дата')}</SelectItem>
                  <SelectItem value="today">{tr('Šodien', 'Today', 'Сегодня')}</SelectItem>
                  <SelectItem value="week">{tr('Šonedēļ', 'This week', 'На этой неделе')}</SelectItem>
                  <SelectItem value="month">{tr('Šomēnes', 'This month', 'В этом месяце')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-xl h-52 animate-pulse" />)}
          </div>
        ) : mode === 'public' ? (
          publicEvents.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E5E4E0] rounded-xl p-12 text-center text-[#5C5C5C]" data-testid="public-event-empty">
              {tr('Nav pasākumu ar šiem filtriem.', 'No events match these filters.', 'Нет событий по этим фильтрам.')}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="public-events-grid">
              {publicEvents.map((ev) => <PublicEventCard key={ev.id} event={ev} />)}
            </div>
          )
        ) : events.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E5E4E0] rounded-xl p-12 text-center text-[#5C5C5C]" data-testid="event-empty">
            {tr('Nav pasākumu.', 'No events found.', 'Событий нет.')}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((ev) => <EventCard key={ev.id} event={ev} />)}
          </div>
        )}
      </div>
    </div>
  );
}
